import {
  ButtonHTMLAttributes,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
} from 'react';
import { styled } from '@mui/material/styles';
import {
  TEXT_BUTTON_DISABLED_COLOR,
  TEXT_BUTTON_HOVER_BG,
  TEXT_BUTTON_PRESSED_BG,
  TEXT_BUTTON_SIZE_SPEC,
  TEXT_BUTTON_TONE_TOKENS,
  type TextButtonSize,
  type TextButtonTone,
} from '@/theme/buttonTokens';

/**
 * 공통 텍스트 버튼 (디자인 가이드 "Text button").
 * 색상·사이즈는 `theme/buttonTokens.ts` 스펙을 사용.
 *
 * 아이콘 크기는 `TEXT_BUTTON_SIZE_SPEC[size].iconSize` 가 **기본값**이다.
 * 호출부가 `width`/`height` 를 명시하면 그쪽이 이긴다 — CSS 로 강제하지 않는 이유는,
 * 결재처럼 반응형으로 아이콘만 따로 키우는 기존 사정(PORTAL-13609)을 깨지 않기 위해서다.
 * 새로 붙이는 호출부는 크기를 넘기지 말 것. 넘긴다면 diff 에 이유가 보여야 한다.
 */

export type TextButtonVariant = TextButtonTone;
export type { TextButtonSize };

export type TextButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> & {
  readonly variant?: TextButtonVariant;
  readonly size?: TextButtonSize;
  readonly underline?: boolean;
  readonly startIcon?: ReactNode;
  readonly endIcon?: ReactNode;
  readonly children: ReactNode;
};

const Root = styled('button', {
  shouldForwardProp: (p) =>
    p !== 'tone' && p !== 'sizeKey' && p !== 'underline',
})<{ tone: TextButtonVariant; sizeKey: TextButtonSize; underline?: boolean }>(
  ({ tone, sizeKey, underline }) => {
    const tokens = TEXT_BUTTON_TONE_TOKENS[tone];
    const size = TEXT_BUTTON_SIZE_SPEC[sizeKey];
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 2,
      padding: 0,
      border: 'none',
      background: 'transparent',
      borderRadius: 4,
      color: tokens.color,
      fontSize: size.fontSize,
      lineHeight: `${size.lineHeight}px`,
      fontWeight: 400,
      cursor: 'pointer',
      transition: 'background-color 120ms ease, color 120ms ease',

      '& > .v2tb-text': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // gap 2 + padding 2 분리는 가이드 구조 — 합치지 말 것
        padding: '0 2px',
        textDecoration: underline ? 'underline' : 'none',
        textDecorationStyle: 'solid',
        textDecorationSkipInk: 'none',
      },

      '&:hover, &:focus, &:focus-visible': {
        background: TEXT_BUTTON_HOVER_BG,
        color: tokens.hoverColor,
        outline: 'none',
      },
      // 보더 없는 버튼이라 포커스 식별 수단이 없음 — 키보드 포커스만 브라우저 기본 링 노출.
      '&:focus-visible': {
        outline: 'auto',
      },
      '&:active': {
        background: TEXT_BUTTON_PRESSED_BG,
        color: tokens.pressedColor,
      },
      '&:disabled': {
        color: TEXT_BUTTON_DISABLED_COLOR,
        cursor: 'not-allowed',
        background: 'transparent',
      },
    };
  },
);

/**
 * 아이콘에 사이즈 스펙 크기를 기본값으로 넣는다.
 * 호출부가 이미 `width`/`height` 를 준 경우엔 건드리지 않는다.
 */
const withDefaultIconSize = (icon: ReactNode, iconSize: number): ReactNode => {
  if (!isValidElement<{ width?: unknown; height?: unknown }>(icon)) return icon;

  const { width, height } = icon.props;
  if (width !== undefined && height !== undefined) return icon;

  return cloneElement(icon, {
    width: width ?? iconSize,
    height: height ?? iconSize,
  });
};

const TextButton = forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    {
      variant = 'default',
      size = 'medium',
      underline = false,
      startIcon,
      endIcon,
      type = 'button',
      children,
      ...rest
    },
    ref,
  ) => {
    const iconSize = TEXT_BUTTON_SIZE_SPEC[size].iconSize;

    return (
      <Root
        ref={ref}
        type={type}
        tone={variant}
        sizeKey={size}
        underline={underline}
        {...rest}
      >
        {withDefaultIconSize(startIcon, iconSize)}
        <span className="v2tb-text">{children}</span>
        {withDefaultIconSize(endIcon, iconSize)}
      </Root>
    );
  },
);

TextButton.displayName = 'TextButton';

export default TextButton;
